/**
 * Hub-mount auto-detection: sections the user finished BY HAND flip to
 * DONE_MANUALLY (existence checks per domain), and a build that died mid-way
 * (stale BUILDING, e.g. tab closed) recovers to PROPOSED. Runs once per
 * event per mount, only for holders of event-setup:write — hooks don't guard
 * writes client-side and a VIEWER's PUT would 403.
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query/queryKeys";
import { getSetupPlan, putSetupPlanSection } from "@/lib/api/clients/EventSetupPlanClient";
import { getAdditionalFields } from "@/lib/api/clients/AdditionalFieldClient";
import { getEmailTemplatesByEvent } from "@/lib/api/clients/EmailTemplateClient";
import { getTableFiltersGuests } from "@/lib/api/clients/TableFilterClient";
import { TableViewType } from "@/types/enums/TableViewType";
import { getWorkflows } from "@/lib/api/clients/WorkflowClient";
import { getOfferings } from "@/lib/slotting/api";
import { getWebsiteConfig } from "@/lib/api/clients/WebsiteConfigClient";
import { getEventGoals } from "@/lib/api/clients/EventGoalClient";
import { getValidGuestCount } from "@/lib/api/clients/GuestClient";
import { seatingInit } from "@/lib/api/clients/SeatingClient";
import { getDevices } from "@/lib/api/clients/CheckInDeviceClient";
import { getDocumentTemplates } from "@/lib/api/clients/DocumentTemplateClient";
import { SetupPlan, SetupSectionKey } from "@/types/SetupPlan";
import { isKnownSectionKey } from "./sectionModules";

/** BUILDING older than this with no local build in flight = crashed build. */
const STALE_BUILDING_MS = 10 * 60 * 1000;

// Module-scoped so the once-per-event existence sweep survives component
// remounts. The onboarding panel mounts/unmounts the setup group as it is
// shown/minimized; a component-local ref would reset on every restore and
// re-fire all ~9 detector GETs (incl. seatingInit). Once per SPA session per
// event is enough — the build/skip paths update the plan directly, and a hard
// reload re-reconciles. Cleared for an event when its plan doc is refetched
// with changes is intentionally NOT done (keeps it cheap; staleness is bounded
// to the session and self-heals on reload).
const detectedEvents = new Set<string>();

/** Test-only: clears the module-level per-event detection latch. */
export function __resetSetupAutoDetectForTests(): void {
  detectedEvents.clear();
}

/**
 * Drops one event from the once-per-session latch so the hook re-detects on its
 * next mount. Called when the user hands a section to Velo to build: the track
 * may unmount (panel minimized) before the Velo drawer closes, so the close-
 * time reconcile can be lost — clearing the latch guarantees a remount re-scans
 * and flips the freshly-built section to DONE_MANUALLY.
 */
export function clearSetupDetectLatch(eventId: string): void {
  detectedEvents.delete(eventId);
}

type Detector = () => Promise<boolean>;

function detectors(eventId: string): Record<SetupSectionKey, Detector> {
  return {
    EMAILS: async () =>
      (await getEmailTemplatesByEvent(eventId, "emailTemplateId")).length > 0,
    WORKFLOWS: async () => (await getWorkflows(eventId)).length > 0,
    OFFERING: async () => (await getOfferings(eventId)).length > 0,
    WEBSITE: async () => (await getWebsiteConfig(eventId)) != null,
    GOALS: async () => {
      const doc = await getEventGoals(eventId);
      return !!doc && (!!doc.goalText?.trim() || (doc.kpis?.length ?? 0) > 0);
    },
    GUESTS: async () => (await getValidGuestCount(eventId, [], false)) > 0,
    ADDITIONAL_FIELDS: async () =>
      (await getAdditionalFields(eventId, "fieldId")).length > 0,
    FILTERS: async () =>
      (
        await getTableFiltersGuests(
          eventId,
          [],
          "tableFilterId",
          0,
          1,
          TableViewType.GUEST,
        )
      ).length > 0,
    SEATING: async () => (await seatingInit(eventId)).rooms.length > 0,
    CHECKIN: async () => (await getDevices(eventId)).length > 0,
    DOCUMENTS: async () => (await getDocumentTemplates(eventId)).length > 0,
  };
}

/**
 * Runs the existence sweep once and reconciles the plan (hand-finished →
 * DONE_MANUALLY, stale BUILDING → PROPOSED), invalidating on change. Exported
 * so callers can force a re-detect imperatively — e.g. after the user built a
 * section through the main Velo drawer, which doesn't touch the plan doc.
 *
 * `visibleKeys` is REQUIRED and must hold exactly the sections whose module the
 * event + role has (see sectionsWithVisibleModule). DONE_MANUALLY is final: a
 * sweep over a section the panel doesn't show would silently close it, and the
 * create-time `proposal` behind it can never be reached again — not even after
 * the plugin is switched back on. Unknown keys are skipped for the same reason
 * the render path drops them (and because "constructor"/"toString" resolve to
 * truthy prototype members, which would PUT a garbage section key).
 */
export async function reconcileSetupSections(
  eventId: string,
  plan: SetupPlan | null | undefined,
  canWrite: boolean,
  queryClient: QueryClient,
  visibleKeys: ReadonlySet<SetupSectionKey>,
  isCancelled: () => boolean = () => false,
): Promise<void> {
  if (!plan || !canWrite) return;
  const detect = detectors(eventId);
  let changed = false;
  for (const section of plan.sections ?? []) {
    if (isCancelled()) break;
    if (!isKnownSectionKey(section.key) || !visibleKeys.has(section.key)) continue;
    try {
      if (section.status === "BUILDING") {
        const changedAt = section.statusChangedAt ? Date.parse(section.statusChangedAt) : NaN;
        if (isNaN(changedAt) || Date.now() - changedAt > STALE_BUILDING_MS) {
          await putSetupPlanSection(eventId, section.key, { status: "PROPOSED" });
          changed = true;
        }
        continue;
      }
      if (section.status !== "PROPOSED" && section.status !== "SKIPPED") continue;
      const exists = await detect[section.key]();
      // Re-check AFTER the await: a build may have started while the detector
      // was in flight — writing DONE_MANUALLY now would race a BUILDING claim.
      if (isCancelled()) break;
      if (exists) {
        await putSetupPlanSection(eventId, section.key, { status: "DONE_MANUALLY" });
        changed = true;
      }
    } catch {
      // Best-effort — a failed check just leaves the section as-is.
    }
  }
  if (changed && !isCancelled()) {
    void queryClient.invalidateQueries({ queryKey: qk.setupPlan.doc(eventId) });
  }
}

export function useSetupAutoDetect(
  eventId: string,
  plan: SetupPlan | null | undefined,
  canWrite: boolean,
  buildInFlight: boolean,
  visibleKeys: ReadonlySet<SetupSectionKey>,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!plan || !canWrite || buildInFlight) return;
    // An empty scope (the event read is still pending — useSetupVisibility
    // returns EMPTY then) has nothing to reconcile; latching on it would skip
    // the existence sweep for the rest of the session.
    if (visibleKeys.size === 0) return;
    if (detectedEvents.has(eventId)) return;
    detectedEvents.add(eventId);

    let cancelled = false;
    let done = false;
    // A3-F-7: sweep from a FRESH read (like build() does) — after a local build
    // the cached plan can still say PROPOSED for the section just BUILT, and its
    // detector would find the new content and flip it to DONE_MANUALLY. One GET
    // per event per session; a failed read falls back to the cached plan.
    void Promise.resolve()
      .then(() => (cancelled ? null : getSetupPlan(eventId))) // no GET for a run already cancelled (A4-F-6)
      .catch(() => null)
      .then((fresh) =>
        cancelled
          ? undefined
          : reconcileSetupSections(
              eventId,
              fresh ?? plan,
              canWrite,
              queryClient,
              visibleKeys,
              () => cancelled,
            ),
      )
      .finally(() => {
        done = true;
      });

    return () => {
      cancelled = true;
      // A sweep cancelled before it finished (a dependency changed identity —
      // e.g. a new visibleKeys Set when the programme read lands) must not burn
      // the latch: the next run sweeps again. Safe — the sweep re-checks
      // isCancelled() after every await, before any PUT (velo wave A2-F-9).
      if (!done) detectedEvents.delete(eventId);
    };
  }, [eventId, plan, canWrite, buildInFlight, queryClient, visibleKeys]);
}
